/**
 * F 154 C2 — the FCC adapter.
 *
 * `paste.ts` is pure and knows nothing about BlockNote. This is the thin layer
 * that supplies the converters and attaches the pipeline to a live editor.
 *
 * The listener runs in the CAPTURE phase and only calls `preventDefault` when
 * it actually recognises a structured source. Plain text is left to BlockNote's
 * own handler, because BlockNote's text path is well tested and replacing it
 * with ours for the common case would be a regression dressed as consistency.
 */

import { useEffect } from 'react';
import type { BlockNoteEditor } from '@blocknote/core';
import { normalisePaste, sanitizePastedHtml, type PasteSource } from './paste';

export interface PasteWiringOptions {
  editor: BlockNoteEditor | null;
  /** Container to listen on. Defaults to the editor's own DOM node. */
  target?: HTMLElement | null;
  /** Called after a successful normalised paste. */
  onNormalised?: (source: PasteSource, blockCount: number) => void;
}

export function usePasteNormalisation({
  editor,
  target,
  onNormalised,
}: PasteWiringOptions): void {
  useEffect(() => {
    if (!editor) return;
    const node: HTMLElement | null =
      target ?? ((editor as any).domElement as HTMLElement | undefined) ?? null;
    if (!node) return;

    const onPaste = (ev: ClipboardEvent) => {
      if (!ev.clipboardData) return;
      const html = ev.clipboardData.getData('text/html');
      const text = ev.clipboardData.getData('text/plain');
      if (!html && !text) return;

      // Only intervene for a structured source. BlockNote already handles
      // plain text well, and hijacking the common case is a regression.
      const probe = normalisePaste(
        { html, text },
        { htmlToBlocks: (h) => editor.tryParseHTMLToBlocks(h), markdownToBlocks: (m) => editor.tryParseMarkdownToBlocks(m) },
      );
      if (probe.source === 'plain') return;

      ev.preventDefault();
      if (probe.blocks.length) {
        // Signature is (blocksToInsert, referenceBlock, placement) — the
        // reference is WHERE, not the document. I had the first two arguments
        // backwards; tsc caught it.
        editor.insertBlocks(
          probe.blocks as any[],
          editor.document[editor.document.length - 1],
          'after',
        );
      }
      onNormalised?.(probe.source, probe.blocks.length);
    };

    node.addEventListener('paste', onPaste, true);
    return () => node.removeEventListener('paste', onPaste, true);
  }, [editor, target, onNormalised]);
}

/** Re-exported so the FCC shell can sanitise a paste before logging it. */
export { sanitizePastedHtml };
